import React from 'react';
import { FaTrash, FaTrashRestore, FaShieldAlt, FaExclamationTriangle } from 'react-icons/fa';

const BACKEND_URL = '';

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 0) return 'just now';
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hr ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day} day${day === 1 ? '' : 's'} ago`;
    const mo = Math.floor(day / 30);
    if (mo < 12) return `${mo} mo ago`;
    return `${Math.floor(mo / 12)} yr ago`;
  } catch {
    return '';
  }
}

export function TrashView({ photos, loading, error, restoringId, purgingId, onRestore, onPurge, onBack }) {
  return (
    <div data-testid="trash-view">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
        <FaShieldAlt className="text-amber-600 text-xl flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-sm">
          <p className="font-semibold text-amber-900 mb-1">Recently Deleted Photos</p>
          <p className="text-amber-800">
            Photos here are kept until you permanently delete them. Only the family owner and Circle Stewards can see this list.
            Restore brings a photo back to the gallery exactly where it was.
          </p>
        </div>
        <button
          onClick={onBack}
          className="text-amber-700 hover:text-amber-900 text-sm font-medium underline whitespace-nowrap"
          data-testid="trash-back-btn"
        >
          Back to Gallery
        </button>
      </div>

      {loading && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center" data-testid="trash-loading">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">Loading deleted photos…</p>
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3" data-testid="trash-error">
          <FaExclamationTriangle className="text-red-600 text-xl flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-900">Could not load trash</p>
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        </div>
      )}

      {!loading && !error && photos.length === 0 && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center" data-testid="trash-empty">
          <FaTrash className="text-gray-300 text-6xl mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-800 mb-2">Trash is empty</h3>
          <p className="text-gray-600">Deleted photos will show up here so you can restore them if you change your mind.</p>
        </div>
      )}

      {!loading && !error && photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map(photo => {
            const isImage = (photo.media_type !== 'video') && !(photo.content_type || '').startsWith('video/');
            const previewUrl = isImage
              ? `${BACKEND_URL}/api/photos/${photo.photo_id}/download`
              : null;
            const isRestoring = restoringId === photo.photo_id;
            const isPurging = purgingId === photo.photo_id;
            return (
              <div
                key={photo.photo_id}
                className="bg-white rounded-xl shadow-md overflow-hidden flex flex-col"
                data-testid={`trash-photo-${photo.photo_id}`}
              >
                <div className="aspect-square bg-gray-100 relative">
                  {isImage ? (
                    <img
                      src={previewUrl}
                      alt={photo.caption || photo.filename || 'Deleted photo'}
                      className="w-full h-full object-cover opacity-60"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <FaTrash className="text-4xl" />
                    </div>
                  )}
                  <div className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
                    Deleted
                  </div>
                </div>
                <div className="p-3 flex flex-col gap-2">
                  <div className="text-xs text-gray-500">
                    {photo.deleted_at ? `Deleted ${timeAgo(photo.deleted_at)}` : 'Recently deleted'}
                  </div>
                  {photo.caption && (
                    <p className="text-sm text-gray-700 line-clamp-2">{photo.caption}</p>
                  )}
                  <div className="flex gap-2 mt-1">
                    <button
                      onClick={() => onRestore(photo.photo_id)}
                      disabled={isRestoring || isPurging}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium"
                      data-testid={`restore-photo-${photo.photo_id}`}
                    >
                      <FaTrashRestore className="text-sm" />
                      {isRestoring ? 'Restoring…' : 'Restore'}
                    </button>
                    <button
                      onClick={() => onPurge(photo.photo_id)}
                      disabled={isRestoring || isPurging}
                      className="flex items-center justify-center px-2 py-2 bg-gray-200 text-red-600 rounded-lg hover:bg-red-100 disabled:opacity-50 text-sm"
                      title="Permanently delete (cannot be undone)"
                      data-testid={`purge-photo-${photo.photo_id}`}
                    >
                      <FaTrash className="text-sm" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TrashView;
